import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, RefreshCw, Users, BookOpen, ExternalLink, ShoppingBag } from "lucide-react";

export default function Licenses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/licenses/mine");
      setItems(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load licenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const copy = (code) => {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Join link copied");
  };

  const regen = async (license_id) => {
    try {
      await api.post(`/licenses/${license_id}/regenerate-code`);
      toast.success("New code generated");
      refresh();
    } catch {
      toast.error("Could not regenerate");
    }
  };

  return (
    <Layout>
      <div className="px-10 py-10 max-w-6xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">Company licenses</div>
            <h1 className="editorial-h1 text-5xl">Seat packs.</h1>
            <p className="text-[#52525B] mt-2 max-w-xl">Every paid pack comes with a shareable link. Send it to your employees — they self-enroll, you track everything.</p>
          </div>
          <Link to="/marketplace" data-testid="buy-more" className="btn-primary"><ShoppingBag size={14}/> Buy more seats</Link>
        </div>

        {loading ? (
          <div className="overline">Loading…</div>
        ) : items.length === 0 ? (
          <div className="editorial-card-static p-16 text-center">
            <Users size={48} strokeWidth={1} className="mx-auto mb-4 text-[#52525B]" />
            <div className="font-serif text-3xl mb-2">No licenses yet.</div>
            <div className="text-[#52525B] mb-6">Buy a course in the marketplace to roll out training to your team.</div>
            <Link to="/marketplace" className="btn-primary" data-testid="licenses-browse">Browse marketplace</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(l => (
              <div key={l.license_id} className="editorial-card-static p-6" data-testid={`license-${l.license_id}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                  <div className="lg:col-span-1">
                    {l.course?.thumbnail && <img src={l.course.thumbnail} className="w-20 h-20 object-cover border border-[#E4E4E7]" alt="" />}
                  </div>
                  <div className="lg:col-span-5">
                    <div className="overline mb-1">{l.course?.category || "Course"}</div>
                    <div className="font-serif text-2xl leading-tight">{l.course?.title}</div>
                    <div className="overline mt-2 text-[#52525B]">Purchased {new Date(l.purchased_at).toLocaleDateString()}</div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="overline mb-1">Seats</div>
                    {l.is_free ? (
                      <>
                        <div className="font-display text-2xl font-bold">{l.seats_used}<span className="text-[#52525B] text-sm"> / unlimited</span></div>
                        <div className="overline mt-1 text-[#E63946]">Free link</div>
                      </>
                    ) : (
                      <>
                        <div className="font-display text-2xl font-bold">{l.seats_used}<span className="text-[#52525B]">/{l.seats_total}</span></div>
                        <div className="mt-1 h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#E63946]" style={{ width: `${100 * l.seats_used / Math.max(l.seats_total, 1)}%` }} /></div>
                      </>
                    )}
                  </div>
                  <div className="lg:col-span-4">
                    <div className="overline mb-1">Join link</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 bg-white border border-[#E4E4E7] font-mono-jb text-xs truncate">
                        {window.location.origin}/join/{l.join_code}
                      </div>
                      <button onClick={() => copy(l.join_code)} data-testid={`copy-${l.license_id}`} title="Copy" className="p-2 border border-[#E4E4E7] hover:bg-[#F9F9F8]"><Copy size={14}/></button>
                      <button onClick={() => regen(l.license_id)} data-testid={`regenerate-${l.license_id}`} title="Regenerate" className="p-2 border border-[#E4E4E7] hover:bg-[#F9F9F8]"><RefreshCw size={14}/></button>
                      <Link to={`/join/${l.join_code}`} target="_blank" className="p-2 border border-[#E4E4E7] hover:bg-[#F9F9F8]"><ExternalLink size={14}/></Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
